import { useState, useRef, useId, useEffect } from 'react'
import { useSchoolSearch } from './useSchoolSearch.js'
import { IconSearch } from '../../components/icons.jsx'
import styles from './SchoolPicker.module.css'
import SchoolTeamCoverage from '../bowl/SchoolTeamCoverage.jsx'

/**
 * Debounced school typeahead. Renders a dropdown of REAL matches from
 * /api/hubspot/schools?q= — each with the school's owned logo, city/state,
 * and verified color swatches. Selecting an option calls onSelect(school)
 * with the fully-normalized record; the parent drives the artwork from it.
 */
export default function SchoolPicker({ onSelect, selectedId }) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const { results, status, error, reset } = useSchoolSearch(query)
  const rootRef = useRef(null)
  const listId = useId()

  // Close on outside click.
  useEffect(() => {
    function onDocClick(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  // Reset the active row whenever the result set changes.
  useEffect(() => {
    setActiveIndex(results.length ? 0 : -1)
  }, [results])

  function choose(school) {
    if (!school) return
    onSelect(school)
    setQuery(school.name)
    setOpen(false)
    reset()
  }

  function onKeyDown(e) {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setOpen(true)
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      if (open && results[activeIndex]) {
        e.preventDefault()
        choose(results[activeIndex])
      }
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  const showDropdown =
    open && query.trim().length >= 2 && status !== 'idle'

  return (
    <div className={styles.picker} ref={rootRef}>
      <div className={styles.inputWrap}>
        <IconSearch className={styles.search} width={17} height={17} />
        <input
          className={`input ${styles.input}`}
          type="text"
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls={listId}
          aria-autocomplete="list"
          placeholder="Search schools by name…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          autoComplete="off"
          spellCheck="false"
        />
        {status === 'loading' && <span className={`spinner ${styles.spin}`} />}
        {status !== 'loading' && query && (
          <button
            type="button"
            className={styles.clear}
            aria-label="Clear search"
            onClick={() => {
              setQuery('')
              reset()
              setOpen(false)
            }}
          >
            ✕
          </button>
        )}
      </div>

      {showDropdown && (
        <div className={styles.dropdown} id={listId} role="listbox">
          {status === 'error' && (
            <div className={`${styles.state} ${styles.stateError}`}>
              {error || 'Search failed.'}
            </div>
          )}

          {status === 'done' && results.length === 0 && (
            <div className={styles.state}>
              No schools match “{query.trim()}”.
            </div>
          )}

          {results.map((school, i) => (
            <div
              key={school.id}
              role="option"
              aria-selected={i === activeIndex || school.id === selectedId}
              className={`${styles.option} ${
                i === activeIndex ? styles.optionActive : ''
              }`}
              onMouseEnter={() => setActiveIndex(i)}
              onMouseDown={(e) => {
                // mousedown (not click) so it fires before input blur
                e.preventDefault()
                choose(school)
              }}
            >
              <SchoolLogo school={school} />
              <div className={styles.meta}>
                <div className={styles.name}>{school.name}</div>
                <div className={styles.loc}>
                  {school.location || 'Location not on file'}
                </div>
                <SchoolTeamCoverage record={school}/>
              </div>
              {school.colors.length > 0 && (
                <div className={styles.swatches} aria-hidden="true">
                  {school.colors.slice(0, 4).map((c, idx) => (
                    <span
                      key={c.hex + idx}
                      className={styles.swatch}
                      style={{ background: c.hex }}
                      title={`${c.label ? c.label + ' · ' : ''}${c.hex}`}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/** Owned logo thumbnail with a monogram fallback. */
function SchoolLogo({ school }) {
  const [failed, setFailed] = useState(false)
  const monogram = school.name?.[0]?.toUpperCase() || 'S'
  if (school.logoUrl && !failed) {
    return (
      <div className={styles.logo}>
        <img
          src={school.logoUrl}
          alt=""
          loading="lazy"
          onError={() => setFailed(true)}
        />
      </div>
    )
  }
  return (
    <div className={styles.logo}>
      <span className={styles.logoFallback}>{monogram}</span>
    </div>
  )
}
